import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { addCode, deleteCode, editCode, getCodes, getSections, setCode } from "./codes.thunks";
import { NewCodeInterface, OkvedSectionInterface } from "./codes.types";

interface CodesState {
	modals: {
		addingCodeModal: {
			visible: boolean;
			values: NewCodeInterface;
		};
		editCodeModal: {
			visible: boolean;
			values: NewCodeInterface;
			isMain: boolean;
		};
		importFileModal: boolean;
	};
	codes: OkvedSectionInterface[];
	sections: Omit<OkvedSectionInterface, "codes">[];
	selectedRows: string[];
	disabledBtn: boolean;
	isEdit: boolean;
	code: {
		code: string;
		delete: boolean;
	};
	error: string;
	loading: boolean;
}

const initialState: CodesState = {
	modals: {
		addingCodeModal: {
			visible: false,
			values: { sectionCode: "", code: "", name: "", description: "" },
		},
		editCodeModal: {
			visible: false,
			values: { sectionCode: "", code: "", name: "", description: "" },
			isMain: false,
		},
		importFileModal: false,
	},
	codes: [],
	sections: [],
	selectedRows: [],
	disabledBtn: true,
	isEdit: false,
	code: {
		code: "",
		delete: false,
	},
	error: "",
	loading: false,
};

const codesSlice = createSlice({
	name: "codesSlice",
	initialState,
	reducers: {
		setAddingModal(
			state,
			action: PayloadAction<{
				visible: boolean;
				inputValues: NewCodeInterface;
			}>,
		) {
			state.modals.addingCodeModal.visible = action.payload.visible;
			state.modals.addingCodeModal.values = action.payload.inputValues;
		},

		setEditCodeModal(
			state,
			action: PayloadAction<{
				visible: boolean;
				inputValues: NewCodeInterface;
				isMain: boolean;
			}>,
		) {
			state.modals.editCodeModal.visible = action.payload.visible;
			state.modals.editCodeModal.values = action.payload.inputValues;
			state.modals.editCodeModal.isMain = action.payload.isMain;
		},

		setImportModal(state, action: PayloadAction<boolean>) {
			state.modals.importFileModal = action.payload;
		},

		selectRow(state, action: PayloadAction<string | { okved: string }[]>) {
			if (!Array.isArray(action.payload)) {
				const candidate = state.selectedRows.includes(action.payload);

				if (!candidate) {
					state.selectedRows.push(action.payload);
				} else {
					const newArr = state.selectedRows.filter((code) => code !== action.payload);

					state.selectedRows = newArr;
				}

				state.disabledBtn = false;
			} else {
				state.selectedRows = action.payload.map((code) => code.okved);
			}
		},

		setIsEdit(state, action) {
			state.isEdit = action.payload;
		},

		setLoading(state, action: PayloadAction<boolean>) {
			state.loading = action.payload;
			state.error = "";
		},

		clearCodes(state) {
			state.codes = [];
		},

		clearError(state) {
			state.error = "";
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getCodes.pending, (state) => {
			state.error = "";
			state.loading = true;
		});
		builder.addCase(getCodes.fulfilled, (state, action) => {
			state.codes = action.payload;
			state.error = "";
			state.loading = false;
		});
		builder.addCase(getCodes.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as any;
		});
		builder.addCase(getSections.pending, (state) => {
			state.error = "";
			state.loading = true;
		});
		builder.addCase(getSections.fulfilled, (state, action: PayloadAction<Omit<OkvedSectionInterface, "codes">[]>) => {
			state.sections = action.payload;
		});
		builder.addCase(addCode.pending, (state) => {
			state.loading = false;
			state.error = "";
		});
		builder.addCase(addCode.fulfilled, (state, action) => {
			const { section, ...rest } = action.payload;
			let newCodes = [...state.codes];
			let findSection = newCodes.find((_) => _.code === action.payload.section);
			if (findSection) {
				findSection = {
					...findSection,
					codes: [...findSection.codes, { ...rest, codes: [] }],
				};
				newCodes = newCodes.map((_) => (_.code === findSection!.code ? findSection : _)) as OkvedSectionInterface[];
			} else {
				newCodes.push({
					code: section,
					name: "",
					description: "",
					codes: [{ ...rest, codes: [] }],
				});
			}
			state.codes = newCodes;
			state.loading = false;
			state.error = "";
		});
		builder.addCase(addCode.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as any;
		});
		builder.addCase(editCode.pending, (state) => {
			state.loading = false;
			state.error = "";
		});
		builder.addCase(editCode.fulfilled, (state, action) => {
			const indexArr = state.codes.map((code) => code.code);
			const candidateIndex = indexArr.indexOf(action.payload.code);

			if (candidateIndex !== -1) {
				state.codes[candidateIndex] = {
					...state.codes[candidateIndex],
					...action.payload,
				};
			}

			state.loading = false;
			state.error = "";
		});
		builder.addCase(editCode.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as any;
		});
		builder.addCase(deleteCode.pending, (state) => {
			state.loading = false;
			state.error = "";
		});
		builder.addCase(deleteCode.fulfilled, (state, action) => {
			const candidate = state.codes.find((code) => code.code === action.payload);

			if (candidate) {
				const newArr = state.codes.filter((code) => code.code !== candidate.code);

				state.codes = [...newArr];
			}

			state.loading = false;
			state.error = "";
		});
		builder.addCase(deleteCode.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as any;
		});
		builder.addCase(setCode.fulfilled, (state, action: PayloadAction<{ code: string; delete: boolean }>) => {
			state.code = action.payload;
		});
	},
});

export const {
	setAddingModal,
	setEditCodeModal,
	clearCodes,
	setImportModal,
	selectRow,
	setLoading,
	setIsEdit,
	clearError,
} = codesSlice.actions;
export default codesSlice.reducer;
